import React from 'react';
import {
  Button,
  createStyles,
  makeStyles,
  Modal,
  Typography,
} from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { useTranslation } from '../../../../../hooks/useTranslation';
import colors from '../../../../../themes/main-colors';
import { Shadow } from '../../../../../constants';
import GreenCheckIcon from '../../../../../static/greenCheckIcon.svg';
import PGEButton from '../../../../buttons/PGE-Button';
import { useIsMobile } from '../../../../../util/style-utils';
import BottomDrawerLayout, {
  DrawerSnapType,
} from '../../bottom-drawer-layout/BottomDrawerLayout';
import useAuth from '../../../../../hooks/useAuth';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modalWrapper: {
      display: 'flex',
      width: '700px',
      maxHeight: '50vh',
      flexDirection: 'column',
      position: 'absolute',
      top: '50%',
      left: '50%',
      overflow: 'auto',
      transform: 'translate(-50%, -50%)',
      padding: theme.spacing(1.5, 3, 1.75, 3),
      backgroundColor: colors.modalBackgroundColor,
      borderRadius: '5px',
      [theme.breakpoints.down('sm')]: {
        top: '50%',
        width: '90%',
        maxHeight: '100%',
        padding: theme.spacing(3),
      },
    },

    closeButton: {
      position: 'absolute',
      top: 6,
      right: 24,
    },
    OutageReportedConfirmTitle: {
      marginLeft: '1rem',
    },

    buttonContainer: {
      display: 'flex',
      padding: '1rem',
      width: '100%',
      justifyContent: 'space-evenly',
    },

    reportOutageHeaderStyle: {
      display: 'flex',
      flexDirection: 'row',
      marginBottom: '1rem',
      alignItems: 'center',
      marginLeft: '1rem',
    },

    reportOutageHeaderStyleMob: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },

    outageReportedContentWhite: {
      background: colors.white,
      padding: '1rem',
    },

    outageReportedConfirmContent: {
      boxShadow: Shadow.ONE,
      maxHeight: '50vh',
    },

    outageReportedAddressDetails: {
      flexDirection: 'column',
      background: '#FFFCD9',
      display: 'flex',
      paddingLeft: '1rem',
      maxHeight: '20vh',
    },
    GreenCheckIconStyle: {
      width: '6%',
    },
    GreenCheckIconStyleMob: {
      width: '10%',
    },
    ReportOutageConfirmCloseButton: {
      padding: theme.spacing(1.625),
      backgroundColor: colors.sparkBlue,
      borderRadius: 5,
      color: colors.white,
      fontSize: '1.125rem',
      marginTop: '2.5rem',
      letterSpacing: 0.36,
      lineHeight: '1.125rem',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        borderRadius: 50,
      },
    },
  }),
);

export interface Props {
  premiseInfo?: string;
  accontNumber: string;
  name: string;
  isAnonymousOrOtherLocation?: boolean;
  onClose: () => void;
  houseNumber?: string;
}

export default function OutageReportedConfirmPopup(props: Props) {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const {
    name,
    accontNumber,
    premiseInfo,
    onClose,
    houseNumber,
    isAnonymousOrOtherLocation,
  } = props;
  const { t } = useTranslation();

  return !isMobile ? (
    <Modal
      open={true}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <div className={classes.modalWrapper}>
        <ReportOutageConfirmHeaderComp />
        <ReportOutageConfirmContentComp
          accountNumber={accontNumber}
          name={name}
          isAnonymousOrOtherLocation={isAnonymousOrOtherLocation}
          houseNumber={houseNumber}
          premiseInfo={premiseInfo}
        />
        <div>
          <PGEButton
            onClick={onClose}
            style={{
              marginLeft: '42%',
              marginTop: '1rem',
              width: '150px',
              fontSize: '1rem',
            }}
          >
            {t('CLOSE')}
          </PGEButton>
        </div>
      </div>
    </Modal>
  ) : (
    <BottomDrawerLayout
      data-testid={'notification-options'}
      refId={0}
      defaultSnap={DrawerSnapType.default}
    >
      <ReportOutageConfirmHeaderComp />
      <ReportOutageConfirmContentComp
        accountNumber={accontNumber}
        name={name}
        isAnonymousOrOtherLocation={isAnonymousOrOtherLocation}
        premiseInfo={premiseInfo}
        houseNumber={houseNumber}
      />
      <Button
        aria-label="report-outage-confirm-close"
        className={classes.ReportOutageConfirmCloseButton}
        onClick={onClose}
      >
        {t('CLOSE')}
      </Button>
    </BottomDrawerLayout>
  );
}

interface ReportOutageConfirmContentCompProps {
  accountNumber: string;
  name: string;
  premiseInfo?: string;
  houseNumber?: string;
  isAnonymousOrOtherLocation?: boolean;
}

function ReportOutageConfirmContentComp({
  accountNumber,
  name,
  premiseInfo,
  houseNumber,
  isAnonymousOrOtherLocation,
}: ReportOutageConfirmContentCompProps) {
  const classes = useStyles();
  const { richT } = useTranslation();
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();
  return (
    <div className={!isMobile ? classes.outageReportedConfirmContent : ''}>
      <div className={classes.outageReportedContentWhite}>
        <Typography variant={'subtitle1'}>
          {richT('OUTAGE_REPORTED_CONFIRM_CONTENT')}
        </Typography>
      </div>

      <div className={classes.outageReportedAddressDetails}>
        <Typography variant={'subtitle1'}>
          {richT('ACCOUNT_REPORTED')}
        </Typography>
        {isAnonymousOrOtherLocation ? (
          <Typography
            variant={'subtitle1'}
            style={{ fontWeight: 600, marginBottom: '0.5rem' }}
          >
            {richT('OUTAGE_STREET_ADDRESS', { HOUSE_NUMBER: houseNumber })}
          </Typography>
        ) : (
          <>
            <Typography variant={'subtitle1'}>
              {accountNumber} {name && '-'} {name}
            </Typography>
            <Typography
              variant={'subtitle1'}
              style={{ fontWeight: 600, marginBottom: '0.5rem' }}
            >
              {premiseInfo}
            </Typography>
          </>
        )}
      </div>
    </div>
  );
}

function ReportOutageConfirmHeaderComp() {
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <div
      className={
        !isMobile
          ? classes.reportOutageHeaderStyle
          : classes.reportOutageHeaderStyleMob
      }
    >
      <GreenCheckIcon
        className={
          !isMobile
            ? classes.GreenCheckIconStyle
            : classes.GreenCheckIconStyleMob
        }
      />
      <Typography
        className={!isMobile ? classes.OutageReportedConfirmTitle : ''}
        variant={'h2'}
      >
        {t('YOUR_OUTAGE_HAS_BEEN_REPORTED')}
      </Typography>
    </div>
  );
}
